/**
 * The teams screen's route registration: the Equipes list, discovered by
 * `../routes.ts`'s glob. The structure editor is a dialog over this page
 * (`StructureDialog`), not a route -- Figma `325:11801`/`678:20157`, owner
 * rulings `325-11801-01/-02/-03` -- so it carries no entry of its own here.
 *
 * `NewTeam` registers no route of its own either: it is its own trigger
 * button and dialog (`FormDialog`), self-gated on `control_plane.write`, and
 * mounts directly inside `Teams`'s header rather than behind a URL.
 *
 * `Teams`'s own header mounts `StructureDialog` uncontrolled, with its own
 * "Estrutura" trigger, the same way it mounts `NewTeam`. The empty state's
 * "Montar do zero" action has no trigger of its own to click, so this
 * file mounts a second, controlled, trigger-less instance here and opens it
 * from `onCreateFirstLevel` -- the two never show at once, since the header's
 * own trigger renders only once a level already exists.
 *
 * Both `StructureDialog` instances render at once whenever a campaign has
 * levels, and share the one `Toaster` `AppShell` already mounts for every
 * route -- `sonner`'s `Toaster` subscribes to a module-level singleton with
 * no `id` scoping, and a second instance here would double-render every
 * toast.
 */

import { createElement, Fragment, useState, type ReactNode } from 'react';
import { Share2 } from 'lucide-react';
import { useParams } from '@tanstack/react-router';

import type { RouteBundle } from '../../routes/contract';
import { ACCOUNTS_PATH } from '../../control/paths';
import { t } from '../i18n';
import { TEAMS_PATH } from '../paths';
import { StructureDialog } from './StructureDialog';
import { Teams } from './Teams';

const CAMPAIGN_PLANE_READ = 'control_plane.read';

function useParamCampaignId(): string {
  const { campaignId } = useParams({ strict: false }) as { campaignId?: string };
  return campaignId ?? '';
}

function TeamsRoute(): ReactNode {
  const campaignId = useParamCampaignId();
  const [structureOpen, setStructureOpen] = useState(false);
  return createElement(
    Fragment,
    null,
    createElement(Teams, { campaignId, onCreateFirstLevel: () => setStructureOpen(true) }),
    createElement(StructureDialog, {
      campaignId,
      open: structureOpen,
      onOpenChange: setStructureOpen,
    }),
  );
}

export const bundle: RouteBundle = {
  routes: [
    {
      path: TEAMS_PATH,
      element: createElement(TeamsRoute),
      requires: CAMPAIGN_PLANE_READ,
      kind: 'data',
    },
  ],
  nav: [],
  campaignNav: [
    {
      path: TEAMS_PATH,
      label: () => t('teams.nav.label'),
      requires: CAMPAIGN_PLANE_READ,
      icon: Share2,
      // Frame 105:6 draws this nested under "Contas monitoradas".
      parent: ACCOUNTS_PATH,
      order: 50,
      kind: 'data',
    },
  ],
};
